import { ready as polyfillsReady } from "../modules/polyfills/dom";

import { initSentry } from "./helpers/sentry";
import { ignoreReplaceStateInHiddenDocument } from "./helpers/history";
import { installGlobalFetch } from "../modules/shared-utils/fetch";

import { captureException } from "@sentry/vue";

import { createApp } from "vue";
import {
  createRouter,
  createWebHistory,
  RouteLocationNormalized,
  RouteRecordRaw,
} from "vue-router";
import { createPinia } from "pinia";
import App from "./App.vue";
import i18n from "./i18n";

import "./assets/styles/fonts.css";
import "./assets/styles/tailwind.css";
import "./assets/styles/reader.scss";
import { providePostHog } from "./helpers/posthog";
import {
  loadFromWpsClient,
  parseContentType,
} from "./components/navigator/loader";
import { provideUILayer } from "./components/UILayers";
import { newForToken, WPSClient } from "./wps/client";
import { AuthType } from "./wps/session";
import { invokeAsync } from "./helpers/async";

import ReaderContainer from "./components/reader/ReaderContainer.vue";

// Prevent PWA install prompt from showing
window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault();
});

if (window.parent !== window || window.opener) {
  invokeAsync(() => import("../modules/embed"));
}

function routeProps(route: RouteLocationNormalized): { wpsClient: WPSClient } {
  let token = route.params.id as string;
  if (!token) {
    return null;
  }

  const instance = route.params.instance as string | undefined;
  if (instance) {
    token = `${instance}.${token}`;
  }

  const wpsClient = newForToken(route.name as AuthType, token);

  // attempt to load the correct vue components based on the content url of the token,
  // or on the embedder's `?preload=` hint until the session confirms the type
  const preload = parseContentType(route.query.preload);
  invokeAsync(() => loadFromWpsClient(wpsClient, preload));

  return { wpsClient };
}

const routes: RouteRecordRaw[] = [
  {
    path: "/:prefix?/",
    component: () => import("./components/Home.vue"),
    name: "test_home",
  },
  {
    path: "/:prefix?/read/:id",
    component: ReaderContainer,
    name: "read",
    props: routeProps,
  },
  {
    path: "/:prefix?/warehouse/sample/:id",
    component: ReaderContainer,
    name: "warehouse_sample",
    props: routeProps,
  },
  {
    path: "/:prefix?/share/:id",
    component: ReaderContainer,
    name: "share",
    props: routeProps,
  },
  {
    path: "/:prefix?/:instance/sample/:id",
    component: ReaderContainer,
    name: "hub_sample",
    props: routeProps,
  },
  /*{
    path: "/warehouse/:id",
    component: () => import("./components/reader/ReaderContainer.vue"),
  },*/
  {
    path: "/:prefix?/:pathMatch(.*)*",
    name: "not-found",
    component: ReaderContainer,
  },
];

ignoreReplaceStateInHiddenDocument();

const router = createRouter({
  history: createWebHistory(),
  strict: true,
  routes,
});

/**
 * This beforeEach allows us to start the WPSClient early
 * this way, we fetch the session a few ms after ReaderContainer.vue start loading
 * instead of waiting for it to be fully loaded.
 * Can save ~100ms
 */
router.beforeEach((to) => routeProps(to) && true);

polyfillsReady
  .catch((error: Error) => error)
  .then((polyfillError) => {
    const pinia = createPinia();

    const app = createApp(App);

    provideUILayer(app);
    providePostHog(app);
    initSentry(app, router);
    installGlobalFetch();

    if (polyfillError) {
      captureException(polyfillError);
    }

    app.use(pinia).use(i18n).use(router).mount("#app");
  });
